import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Phone } from "lucide-react";
import { trackLead } from "@/utils/gtag";
import { Logo } from "@/components/Logo";
import { Step1Addresses, Step2MoveType, Step3PropertySize, Step5Truck } from "@/components/booking/wizard/steps";
import { StepPropertyDetails, Step7DateTime, Step8Extras } from "@/components/booking/wizard/details";
import { Step6Inventory, Step9Customer, Step10Summary } from "@/components/booking/wizard/final";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = 10;

const SIZE_TO_BEDROOMS = { "Studio": "1", "1 Bedroom": "1", "2 Bedroom": "2", "3 Bedroom": "3", "4 Bedroom": "4", "5+ Bedroom": "4+", "Office": "3", "Not Sure": "2" };

const emptyProp = { type: "", floor: "Ground Floor", lift: "No", stairs: "No", stair_flights: 1, truck_access: "Yes" };

const Success = ({ mode, result }) => (
  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="rounded-3xl border border-blaze/40 bg-white p-10 text-center shadow-xl" data-testid="booking-success">
    <CheckCircle2 className="mx-auto mb-6 h-16 w-16 text-blaze" strokeWidth={1.4} />
    {mode === "booking" ? (
      <>
        <h2 className="font-display text-2xl font-semibold uppercase tracking-tight sm:text-3xl">Booking confirmed</h2>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-600">
          Thanks {result.first_name} — your move on {result.moving_date} at {result.start_time} is booked. A confirmation email is on its way to {result.email}.
        </p>
        <div className="mx-auto mt-8 max-w-xs rounded-2xl border border-blaze bg-blaze/10 p-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-600">Your Booking Number</p>
          <p className="mt-1 font-display text-4xl font-semibold tracking-tight text-blaze" data-testid="booking-job-id">{result.job_id}</p>
        </div>
      </>
    ) : (
      <>
        <h2 className="font-display text-2xl font-semibold uppercase tracking-tight sm:text-3xl">Quote request received</h2>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-600">
          Thanks {result.first_name || "there"} — your quote has been emailed to {result.email}. Our team will call you shortly to confirm availability and lock in your booking.
        </p>
        <div className="mx-auto mt-8 max-w-xs rounded-2xl border border-blaze bg-blaze/10 p-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-600">Your Quote Number</p>
          <p className="mt-1 font-display text-4xl font-semibold tracking-tight text-blaze" data-testid="quote-number-result">{result.quote_number}</p>
        </div>
      </>
    )}
    <a href="/" className="mt-10 inline-block rounded-2xl border border-slate-300 px-6 py-3 text-xs font-bold uppercase tracking-widest text-slate-900 transition-colors hover:border-blaze hover:text-blaze" data-testid="booking-home-btn">Back to Home</a>
  </motion.div>
);

export default function Booking({ mode = "quote" }) {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [estError, setEstError] = useState(false);
  const [data, setData] = useState({
    pickup_suburb: searchParams.get("pickup_suburb") || "",
    drop_suburb: searchParams.get("drop_suburb") || "",
    move_type: "",
    property_size: "",
    truck_choice: "",
    movers_choice: null,
    pickup_prop: { ...emptyProp },
    drop_prop: { ...emptyProp },
    inventory: [], inventory_skipped: false,
    moving_date: searchParams.get("moving_date") || "",
    start_time: "",
    extra_services: [],
    boxes_qty: 0, stairs_qty: 0,
    first_name: "", last_name: "",
    mobile: searchParams.get("mobile") || "",
    email: searchParams.get("email") || "",
    notes: "",
    travel_minutes: null, travel_km: null, callout_hours: null, callout_pickup_km: null, callout_drop_km: null,
  });
  const patch = (p) => setData((d) => ({ ...d, ...p }));

  useEffect(() => {
    axios.get(`${API}/settings/booking`).then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const fetchTravel = (pickup, drop) => {
    axios.post(`${API}/travel`, { pickup, drop })
      .then(({ data: r }) => patch({ travel_minutes: r.minutes, travel_km: r.km, callout_hours: r.callout_hours, callout_pickup_km: r.pickup_cbd_km, callout_drop_km: r.drop_cbd_km }))
      .catch(() => patch({ travel_minutes: null, travel_km: null, callout_hours: null, callout_pickup_km: null, callout_drop_km: null }));
  };

  const fetchEstimate = () => {
    setEstimate(null);
    setEstError(false);
    axios.post(`${API}/estimate`, {
      property_size: data.property_size || "Not Sure",
      move_type: data.move_type,
      inventory: data.inventory,
      travel_minutes: data.travel_minutes,
      callout_hours: data.callout_hours || 0,
      extra_services: data.extra_services,
      truck_id: data.truck_choice || null,
      movers: data.movers_choice || null,
      boxes: data.boxes_qty,
      stairs: data.stairs_qty,
    }).then(({ data }) => setEstimate(data)).catch(() => setEstError(true));
  };

  useEffect(() => {
    if (step !== 10) return;
    fetchEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const next = () => { setStep((s) => Math.min(TOTAL_STEPS, s + 1)); window.scrollTo({ top: 0 }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0 }); };
  const nextFromStep1 = () => { fetchTravel(data.pickup_suburb, data.drop_suburb); next(); };

  const submit = async (secure) => {
    if (!estimate) return;
    setSubmitting(true);
    const extrasLabels = (settings?.extra_services || []).filter((s) => data.extra_services.includes(s.id)).map((s) => s.label);
    if (data.boxes_qty > 0) extrasLabels.push(`Line Boxes × ${data.boxes_qty}`);
    if (data.stairs_qty > 0) extrasLabels.push(`Flights of Stairs × ${data.stairs_qty}`);
    try {
      if (secure) {
        const payload = {
          pickup_suburb: data.pickup_suburb, pickup_address: "",
          drop_suburb: data.drop_suburb, drop_address: "",
          moving_date: data.moving_date,
          bedrooms: SIZE_TO_BEDROOMS[data.property_size] || "2",
          property_size: data.property_size,
          property_type: data.pickup_prop.type || "House",
          truck: estimate.truck.name, rate: estimate.truck.rate, min_hours: estimate.hours.min_hours,
          discounted: false,
          inventory: data.inventory.map((i) => `${i.name} ×${i.qty}`),
          movers: estimate.truck.movers,
          travel_minutes: data.travel_minutes, travel_km: data.travel_km, travel_manual: false,
          loading_hours: estimate.hours.loading, unloading_hours: estimate.hours.unloading,
          travel_hours: estimate.hours.travel, callout_hours: data.callout_hours || 0,
          total_hours: estimate.hours.billable + (data.callout_hours || 0),
          estimated_price: estimate.costs.total,
          start_time: data.start_time,
          first_name: data.first_name, last_name: data.last_name,
          mobile: data.mobile, email: data.email, notes: data.notes,
          move_type: data.move_type,
          pickup_details: data.pickup_prop, drop_details: data.drop_prop,
          extra_services: extrasLabels,
        };
        const { data: booking } = await axios.post(`${API}/bookings`, payload);
        try {
          const { data: fee } = await axios.post(`${API}/jobs/${booking.job_id}/booking-fee/card`, { origin_url: window.location.origin });
          if (fee.checkout_url) {
            trackLead("booking_completed");
            window.location.href = fee.checkout_url;
            return;
          }
        } catch { /* fall through to confirmation screen */ }
        setDone({ ...booking, _mode: "booking" });
      } else {
        const payload = {
          pickup_suburb: data.pickup_suburb, drop_suburb: data.drop_suburb,
          pickup_address: "", drop_address: "",
          moving_date: data.moving_date,
          property_size: data.property_size || "Not specified",
          bedrooms: SIZE_TO_BEDROOMS[data.property_size] || "2",
          property_type: data.pickup_prop.type || "",
          phone: data.mobile, email: data.email,
          customer_name: `${data.first_name} ${data.last_name}`.trim(),
          truck_size: estimate.truck.name, rate: estimate.truck.rate,
          min_hours: estimate.hours.min_hours,
          estimated_hours: estimate.hours.billable + (data.callout_hours || 0),
          estimated_total: estimate.costs.total,
          callout_hours: data.callout_hours || 0,
          callout_pickup_km: data.callout_pickup_km || 0,
          callout_drop_km: data.callout_drop_km || 0,
          inventory: data.inventory.map((i) => `${i.name} ×${i.qty}`),
          preferred_time: data.start_time, time_window: data.start_time,
          special_requirements: data.notes,
          move_type: data.move_type,
          pickup_details: data.pickup_prop, drop_details: data.drop_prop,
          extra_services: extrasLabels,
        };
        const { data: q } = await axios.post(`${API}/quotes/full`, payload);
        setDone({ ...q, first_name: data.first_name, email: data.email, _mode: "quote" });
      }
      trackLead(secure ? "booking_completed" : "quote_submitted");
      window.scrollTo({ top: 0 });
    } catch {
      toast.error("Could not submit. Please try again or call 03 9113 6005.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50" data-testid="booking-page">
      <div className="fixed inset-x-0 top-0 z-40 border-b border-line bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-5 py-3">
          <a href="/" aria-label="MoversNearby home"><Logo iconSize="h-7" textSize="text-sm" /></a>
          {!done && <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400" data-testid="wz-step-counter">Step {step} of {TOTAL_STEPS}</span>}
          <a href="tel:0391136005" className="flex items-center gap-1.5 text-xs font-bold text-blaze" data-testid="wz-call"><Phone className="h-4 w-4" /> <span className="hidden sm:inline">03 9113 6005</span></a>
        </div>
        {!done && (
          <div className="h-1 bg-line/60">
            <motion.div className="h-1 bg-blaze" animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }} transition={{ duration: 0.4 }} data-testid="wz-progress" />
          </div>
        )}
      </div>

      <section className="mx-auto max-w-2xl px-5 pb-20 pt-24">
        {done ? (
          <Success mode={done._mode || mode} result={done} />
        ) : !settings ? (
          <p className="py-20 text-center text-sm text-slate-500">Loading…</p>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              {step === 1 && <Step1Addresses data={data} patch={patch} onNext={nextFromStep1} />}
              {step === 2 && <Step2MoveType data={data} patch={patch} onNext={next} onBack={back} />}
              {step === 3 && <Step3PropertySize data={data} patch={patch} onNext={next} onBack={back} />}
              {step === 4 && <StepPropertyDetails title="Tell us about your pickup location" value={data.pickup_prop} onChange={(v) => patch({ pickup_prop: v })} onNext={next} onBack={back} testid="wz-step4" />}
              {step === 5 && <Step5Truck data={data} patch={patch} settings={settings} onNext={next} onBack={back} />}
              {step === 6 && <Step6Inventory data={data} patch={patch} onNext={next} onBack={back} />}
              {step === 7 && <Step7DateTime data={data} patch={patch} settings={settings} onNext={next} onBack={back} />}
              {step === 8 && <Step8Extras data={data} patch={patch} settings={settings} onNext={next} onBack={back} />}
              {step === 9 && <Step9Customer data={data} patch={patch} onNext={next} onBack={back} />}
              {step === 10 && <Step10Summary data={data} estimate={estimate} estError={estError} onRetryEstimate={fetchEstimate} settings={settings} onBack={back} onSecure={() => submit(true)} onQuote={() => submit(false)} submitting={submitting} />}
            </motion.div>
          </AnimatePresence>
        )}
      </section>
    </main>
  );
}
